<script setup lang="ts">
/**
 * 多 Tab 栏。
 *
 * <h2>实现</h2>
 * 用 naive-ui 的 `n-tabs`（`type="card"` + 可关闭）。tab 数据全在 `useTabsStore`，
 * 这里只负责**展示 + 把用户操作回灌给 store / router**：
 *  - 点 tab → `update:value` → `router.push(activeRoute())`
 *  - 点 ×  → `close`（带 name）→ `store.close` → 必要时 `router.push` 到相邻 tab
 *
 * <h2>⚠️ 不在这里持路由状态</h2>
 * 当前激活 tab 来自 `store.activeKey`，而它由 `AppShell` 里的 `watch($route)` 维护。
 * ⇒ 这一层是「受控」的，⛔ 不自己猜当前是哪个 tab。
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { NButton, NTabs, NTab } from 'naive-ui'
import { useTabsStore } from '@/stores/tabs'

const store = useTabsStore()
const router = useRouter()

const value = computed({
  get: () => store.activeKey,
  set: (key: string) => {
    // 切 tab：push 到那个 tab 记录的当前 fullPath
    store.activeKey = key
    const target = store.tabs.find((t) => t.key === key)
    if (target) void router.push(target.fullPath)
  },
})

/**
 * 关 tab。naive-ui 的 `close` 回调签名是 `(name: string | number)`。
 * - 关的是当前激活 tab：store.close 返回应跳转的 fullPath，push 过去；
 * - 否则只删数组项。
 * - 🔴 工作台不可关（store 内已挡）。
 */
function onClose(name: string | number) {
  const key = String(name)
  const next = store.close(key)
  if (next) void router.push(next)
}

/**
 * 关闭除当前 tab 和工作台以外的所有 tab。
 * 🔴 按钮只在「有可关掉的其它 tab」时出现 —— 只有工作台/当前 tab 时不显示，
 *    避免出现一个点了什么都不会发生的按钮。
 */
const otherClosableCount = computed(
  () =>
    store.tabs.filter((t) => !store.isPersistent(t.key) && t.key !== store.activeKey)
      .length,
)

function onCloseOthers() {
  const next = store.closeOthers()
  if (next) void router.push(next)
}
</script>

<template>
  <div class="tabbar-wrap">
    <n-tabs
      v-model:value="value"
      type="card"
      :tabs-padding="12"
      size="medium"
      @close="onClose"
      class="tabbar"
    >
      <n-tab
        v-for="t in store.tabs"
        :key="t.key"
        :name="t.key"
        :tab="t.title"
        :closable="!store.isPersistent(t.key)"
      />
    </n-tabs>

    <!-- 🔴 右侧「关闭其他」按钮 —— 只在确有可关的其它 tab 时出现，避免空操作按钮 -->
    <n-button
      v-if="otherClosableCount > 0"
      size="tiny"
      quaternary
      class="close-others"
      title="关闭除当前和工作台以外的所有标签"
      @click="onCloseOthers"
    >
      关闭其他
    </n-button>
  </div>
</template>

<style scoped>
/* tab 栏 + 右侧按钮同一行：tab 占满，按钮贴右 */
.tabbar-wrap {
  display: flex;
  align-items: flex-end;
  gap: 4px;
}

.tabbar {
  flex: 1 1 auto;
  min-width: 0;
  /*
    ⚠️ 贴在内容区上方：tab 栏自己是一张浅卡，内容区往下走。

    🔴 选中态用两套手段叠加（naive-ui 的 card 型选中 tab 有坑，单靠变量不够）：

    ① CSS 变量（对 color / font-weight 生效）：
       --n-tab-text-color-active → 白字
       --n-tab-font-weight-active → 600
       --n-tab-color → 未选中 tab 的底色，给一档白，让它从灰地面里浮出来。

    ② ⚠️ naive-ui 的 card 型「选中 tab」把 background-color 硬写成 #0000（透明），
       意图是让选中卡「透出内容区背景、和内容连成一体」。但在本工程里内容区背景是
       灰地面（--canvas），于是选中 tab 反而灰扑扑地融进地面、读不出「被选中」。
       ⇒ 必须用 :deep 在选中 tab 上把背景再写回实色蓝，变量那条路被透明覆盖拦死了。

    ⚠️ 这里「蓝底白字」是用户明确要的醒目选中态，不是正文级文字（全局底座那条
       「蓝底墨字」是针对内容文案的，导航选中态不在此例）。
  */
  --n-tab-padding: 7px 14px;
  --n-tab-font-weight-active: 600;
  --n-tab-text-color-active: var(--surface);
  --n-tab-text-color-hover: var(--text);
  --n-tab-color: var(--surface);
  --n-tab-border-color: var(--line);
}

/*
  🔴 选中 tab：实底蓝 + 白字 + 加粗，且与内容区连成一体（无缝隙）。

  连体原理：naive-ui 的顶部 card 型选中 tab 已自带 `border-bottom: 1px solid #0000`
  （底边透明），同时 nav 容器的底部分隔线在选中 tab 正下方会断开 —— 即它本就是
  「与内容连体」的设计。原本连体失败是因为选中 tab 透明、露出灰地面显得割裂；
  现在给选中 tab 补上实底蓝，连体效果自然成立，不需要再去动 nav 的边框。
*/
.tabbar :deep(.n-tabs-tab.n-tabs-tab--active) {
  background-color: var(--brand-500);
  border-color: var(--brand-500);
}

/* hover 2026-09-09 用户反馈：原来悬停加深到 brand-600（#0958d9 深蓝）显闷；
   改为悬停变亮一档（brand-400），与「选中=实底蓝」同族但不发暗 */
.tabbar :deep(.n-tabs-tab.n-tabs-tab--active:hover) {
  background-color: var(--brand-400);
  border-color: var(--brand-400);
}

/* 运行时 active card 仍是白底，关闭图标必须使用可见的品牌深色。 */
.tabbar :deep(.n-tabs-tab.n-tabs-tab--active .n-base-close) {
  color: var(--brand-600);
}

/* 可见图标仍保持轻量，但按钮本身满足触屏 44px 目标。 */
.tabbar :deep(.n-base-close) {
  width: 44px;
  height: 44px;
  margin: -5px -9px -5px 2px;
  opacity: 1;
}

/* 按钮贴在 tab 栏右下，与 tab 文字基线大致对齐；
   ⚠️ 底部留一点空隙，不跟 tab 自带的下边框挤在一起 */
.close-others {
  flex: 0 0 auto;
  margin: 0 8px 6px 0;
  white-space: nowrap;
}
</style>
